<template>
    <div class="chat-window-manager">
        <MultiChatViewer v-if="multiChatView.enabled" @close="closeMultiChatView" />
        <GridChatWindowManager
            v-if="activeView === VIEWS.CHAT"
            ref="gridWindow"
            :contacts="chatContacts"
            :contact="selectedContact"
            :loading="loading"
            @loadMoreContacts="loadMoreContacts"
        />
        <FilterPanel v-if="activeView === VIEWS.FILTER" :filterId="filterId" />
        <TableContactList v-if="activeView === VIEWS.LIST" :filterId="filterId" />
        <KanbanContactList v-if="activeView === VIEWS.KANBAN" :filterId="filterId" />
        <ContactSwitchScreen v-if="activeView === VIEWS.TAB" @close="() => setActiveView(VIEWS.CHAT)" />
        <ChatWindowManagerTool v-if="!disabledTools.includes(activeView) && !multiChatView.enabled" />
    </div>
</template>
<script>
import { mapActions, mapGetters, mapState } from "vuex";
import * as _ from "lodash";
import GridChatWindowManager from "./GridChatWindowManager.vue";
import FilterPanel from "./FilterPanel.vue";
import TableContactList from "./TableContactList.vue";
import KanbanContactList from "./KanbanContactList.vue";
import ContactSwitchScreen from "./ContactSwitchScreen.vue";
import MultiChatViewer from "./multi-chat-viewer/MultiChatViewer.vue";
import log2 from "../../../utils/log2";
import ChatWindowManagerTool from "./ChatWindowManagerTool.vue";

const log = log2("ChatWindowManager");

const VIEWS = {
    CHAT: "chat",
    TAB: "tab",
    FILTER: "filter",
    LIST: "list",
    KANBAN: "kanban",
};

export default {
    name: "ChatWindowManager",
    components: {
        MultiChatViewer,
        ContactSwitchScreen,
        GridChatWindowManager,
        ChatWindowManagerTool,
        TableContactList,
        KanbanContactList,
        FilterPanel,
    },
    data: () => ({
        VIEWS,
        disabledTools: [VIEWS.FILTER, VIEWS.TAB],
        contacts: [],
        loading: false,
        nextToken: null,
        previousView: null,
    }),
    mounted() {
        const keydownHandler = (e) => {
            const isInput = e.target?.tagName === "INPUT" || e.target?.tagName === "TEXTAREA";
            const rawTarget = e.target;
            const targetEl =
                rawTarget && rawTarget.nodeType === 3 ? rawTarget.parentElement : rawTarget;
            const isInContentEditable =
                targetEl && targetEl.closest ? !!targetEl.closest("[contenteditable='true']") : false;

            // Layout değiştirme kısayolları (Ctrl + 1/2/3) - her zaman çalışır
            if (e.ctrlKey && !e.altKey && !e.metaKey && !e.shiftKey) {
                if (e.key === "1") {
                    e.preventDefault();
                    this.setActiveView(VIEWS.CHAT);
                    return;
                }
                else if (e.key === "2") {
                    e.preventDefault();
                    this.setActiveView(VIEWS.LIST);
                    return;
                }
                else if (e.key === "3") {
                    e.preventDefault();
                    this.setActiveView(VIEWS.KANBAN);
                    return;
                }
            }

            // Tab ve Escape için özel kontroller
            const editor = _.get(
                this.$refs.gridWindow,
                "$refs.chatWindow.$refs.chat.$refs.messageArea.$refs.messageArea.$refs.editor"
            );
            const isTabEnabled = editor?.isTabEnabled();

            // Mesaj editörü (ProseMirror) veya başka rich text: Tab burada işlensin, TAB ekranı açılmasın
            if (e.key === "Tab" && !isTabEnabled && !isInput && !isInContentEditable) {
                if (this.activeView === VIEWS.TAB) {
                    // Ekran açıkken Tab tuşunu ContactSwitchScreen'e bırak (gruplar arası geçiş için)
                    // ESC tuşu ile kapatılacak
                    return;
                } else {
                    // Ekran kapalıyken Tab tuşu ile aç
                    this.$feature.beforeOpenLiveScore(() => {
                        this.setActiveView(VIEWS.TAB);
                        const allContactsFilter = this.contactFilters.find((filter) => filter.key === "allContacts");
                        if(allContactsFilter){
                            this.setSelectedFilterId(allContactsFilter.id);
                        }
                    });
                    e.preventDefault();
                }
            }

            // ESC tuşu: Ekranı kapat
            if (e.key === "Escape") {
                if (this.multiChatView?.enabled) return;
                if (this.activeView === VIEWS.TAB) {
                    this.setActiveView(VIEWS.CHAT);
                    e.preventDefault();
                }
            }
        };
        window.addEventListener("keydown", keydownHandler);
        this.$once("hook:beforeDestroy", () => {
            window.removeEventListener("keydown", keydownHandler);
        });
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            site: (state) => state.site.site,
            isRoot: (state) => state.auth.user.role.isRoot,
            fakeMode: (state) => state.app.fakeMode,
            selectedContact: (state) => state.contact.selectedContact,
            multiChatView: (state) => state.contact.multiChatView,
            activeView: (state) => state.contact.activeView,
            route: (state) => state.app.route,
            contactFilters: (state) => state.contact.filters,
            selectedState: (state) => state.contact.selectedState,
            selectedSortField: (state) => state.contact.selectedSortField,
            selectedSortDir: (state) => state.contact.selectedSortDir,
            paginationCursor: (state) => state.contact.paginationCursor,
        }),
        ...mapGetters({
            findConversationByVisitorId: "chat/findConversationByVisitorId",
            isDarkMode: "user/isDarkMode",
            findConversation: "chat/findConversation",
            selectedFilter: "contact/selectedFilter",
        }),
        chatContacts() {
            // Listeyi tek kaynaktan yönetmek için contact store kullanılıyor
            // Tablet LIST görünümü kendi içinde; CHAT görünümü bu listeyi kullanır
            return _.get(this.$store, "state.contact.contacts", []);
        },
        filterType() {
            return this.selectedFilter?.type;
        },
        filterId() {
            return this.selectedFilter?.id;
        },
    },
    methods: {
        ...mapActions({
            setMultiChatView: "contact/setMultiChatView",
            setActiveView: "contact/setActiveView",
            fetchContacts: "contact/fetchContacts",
            getFilteredContacts: "contact/getFilteredContacts",
            setSelectedFilterId: "contact/setSelectedFilterId",
        }),
        getContacts(appendContacts = false, resetNextToken = false) {
            let nextToken = this.paginationCursor.nextToken;
            if (resetNextToken) nextToken = null;
            if (appendContacts && !nextToken) {
                return;
            }
            if (this.filterId && this.activeView === VIEWS.CHAT) {
                this.loading = true;
                this.getFilteredContacts({
                    nextToken,
                    source: "ChatWindowManager.getContacts",
                    appendContacts: nextToken && appendContacts,
                    count: true,
                })
                    .then((data) => {
                        this.loading = false;
                    })
                    .catch((error) => {
                        this.loading = false;
                    });
            }
        },
        loadMoreContacts() {
            this.getContacts(true);
        },
        closeMultiChatView() {
            this.setMultiChatView({
                enabled: false,
                type: "none",
                contacts: [],
            });
        },
    },
    watch: {
        "route.query.view": {
            immediate: true,
            deep: true,
            handler(newVal, oldVal) {
                if (newVal === oldVal) return;
                const view = this.$route.query.view;
                const validViews = Object.values(VIEWS).includes(view);
                if (validViews) {
                    this.setActiveView(view);
                } else {
                    this.setActiveView(VIEWS.CHAT);
                }
            },
        },
        "route.query.inbox": {
            immediate: true,
            deep: true,
            handler(newVal, oldVal) {
                if (newVal === oldVal) return;
                const activeTab = this.$route.query.tab;
                if (activeTab === "inbox" && !newVal && ![VIEWS.TAB].includes(this.activeView)) {
                    this.setActiveView(VIEWS.CHAT);
                }
            },
        },
        activeView: {
            immediate: true,
            deep: true,
            handler(newVal, oldVal) {
                if (newVal === oldVal) return;

                // Layout geçişlerinde liste yenileme mantığı
                const prev = this.previousView;
                const current = newVal;

                // kanban -> chat: listeyi yeniden çek
                if (prev === VIEWS.KANBAN && current === VIEWS.CHAT) {
                    this.$nextTick(() => {
                        this.getContacts(false, true);
                    });
                }

                // table -> chat: listeyi yeniden çek
                if (prev === VIEWS.LIST && current === VIEWS.CHAT) {
                    this.$nextTick(() => {
                        this.getContacts(false, true);
                    });
                }

                // Önceki view'ı sakla
                this.previousView = newVal;

                const validViews = Object.values(VIEWS).includes(newVal);
                if (validViews) {
                    const invalidInboxViews = [VIEWS.TAB];
                    const query = {
                        ...this.$route.query,
                        view: newVal,
                    };
                    if (invalidInboxViews.includes(newVal)) {
                        query.inbox = undefined;
                    } else {
                        let inbox = this.$route.query.inbox;
                        if (!inbox && this.contactFilters[0]) {
                            inbox = this.contactFilters[0].key;
                        }
                        query.inbox = inbox;
                    }
                    this.$router.replace({ query });
                } else {
                    this.$router.replace({ query: { ...this.$route.query, view: VIEWS.CHAT } });
                }
            },
        },
    },
};
</script>
<style>
.chat-window-manager {
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
}
</style>
